<template>
  <div class="series-filter">
    <BDropdown
      :text="buttonText"
      variant="outline-secondary"
      class="series-filter__dropdown"
      menu-class="series-filter__menu scroll-y scroll-on-hover"
      @show="handleShow"
      @hide="handleHide"
    >
      <template #button-content>
        <span>{{ buttonText }}</span>
        <ChevronDown :size="16" class="ms-2" />
      </template>

      <div class="series-filter__content">
        <div class="series-filter__list">
          <BFormCheckbox
            v-for="(option, index) in displayedOptions"
            :key="option.value"
            v-model="internalSelected"
            :value="option.value"
            class="series-filter__option"
          >
            {{ option.label }}
          </BFormCheckbox>

          <div v-if="hasMore" class="series-filter__more">
            <BButton
              variant="link"
              size="sm"
              class="series-filter__more-button"
              @click="toggleShowAll"
            >
              <span>{{ showAll ? lessText : moreText }}</span>
              <ChevronDown
                :size="16"
                class="ms-1"
                :class="{ 'rotate-180': showAll }"
              />
            </BButton>
          </div>
        </div>
      </div>
    </BDropdown>
  </div>
</template>

<script>
import { ChevronDown } from "lucide-vue-next";

export default {
  name: "SeriesFilter",

  components: {
    ChevronDown,
  },

  props: {
    label: {
      type: String,
      default: "Serie",
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [
        { label: "Galaxy Z", value: "galaxy-z" },
        { label: "Galaxy S", value: "galaxy-s" },
        { label: "Galaxy A", value: "galaxy-a" },
        { label: "Galaxy XCover", value: "galaxy-xcover" },
        { label: "Certified Re-Newed", value: "certified-re-newed" },
      ],
    },
    collapsedCount: {
      type: Number,
      default: 4,
    },
    moreText: {
      type: String,
      default: "Mehr anzeigen",
    },
    lessText: {
      type: String,
      default: "Weniger anzeigen",
    },
    showCount: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["update:modelValue"],

  data() {
    return {
      showAll: false,
      internalSelected: [...this.modelValue],
    };
  },

  computed: {
    buttonText() {
      if (this.showCount && this.internalSelected.length > 0) {
        return `${this.label} (${this.internalSelected.length})`;
      }
      return this.label;
    },

    displayedOptions() {
      if (this.showAll) {
        return this.options;
      }
      return this.options.slice(0, this.collapsedCount);
    },

    hasMore() {
      return this.options.length > this.collapsedCount;
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        this.internalSelected = [...newVal];
      },
    },

    internalSelected: {
      deep: true,
      handler(newVal) {
        this.$emit("update:modelValue", newVal);
      },
    },
  },

  methods: {
    toggleShowAll() {
      this.showAll = !this.showAll;
    },

    handleShow() {
      this.$emit("show");
    },

    handleHide() {
      this.$emit("hide");
    },
  },
};
</script>

<style lang="scss" scoped>
.series-filter {
  &__dropdown {
    :deep(.btn) {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-width: 180px;
      padding: 0.5rem 1rem;
      border-radius: var(--radius-lg);
      border: 1px solid var(--bs-border-color);
      background-color: var(--bs-body-bg);
      color: var(--bs-body-color);
      transition: all 0.2s ease;

      &:hover {
        border-color: var(--bs-gray-400);
        background-color: var(--bs-gray-100);
      }

      &:focus,
      &:active {
        border-color: var(--bs-primary);
        box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25);
      }
    }
  }

  &__menu {
    min-width: 280px;
    max-width: 360px;
    padding: 0.75rem;
    border-radius: var(--radius-lg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid var(--bs-border-color);
    // max-height: 400px;
  }

  &__content {
    // max-height: 400px;
    // overflow-y: auto;

    // &::-webkit-scrollbar {
    //   width: 6px;
    // }

    // &::-webkit-scrollbar-track {
    //   background: var(--bs-gray-100);
    //   border-radius: var(--radius-sm);
    // }

    // &::-webkit-scrollbar-thumb {
    //   background: var(--bs-gray-400);
    //   border-radius: var(--radius-sm);

    //   &:hover {
    //     background: var(--bs-gray-500);
    //   }
    // }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__option {
    :deep(.form-check-input) {
      width: 1.25rem;
      height: 1.25rem;
      border-radius: var(--radius-sm);
      border: 2px solid var(--bs-gray-400);
      cursor: pointer;
      transition: all 0.2s ease;

      &:checked {
        background-color: var(--bs-primary);
        border-color: var(--bs-primary);
      }

      &:focus {
        border-color: var(--bs-primary);
        box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25);
      }
    }

    :deep(.form-check-label) {
      cursor: pointer;
      user-select: none;
      padding-left: 0.5rem;
      font-size: 0.875rem;
      color: var(--bs-body-color);
    }

    &:hover {
      :deep(.form-check-input) {
        border-color: var(--bs-primary);
      }
    }
  }

  &__more {
    margin-top: 0.5rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--bs-border-color);
  }

  &__more-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--bs-primary);
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
      color: var(--bs-primary);
      text-decoration: underline;
      background-color: var(--bs-gray-100);
    }

    svg {
      transition: transform 0.2s ease;

      &.rotate-180 {
        transform: rotate(180deg);
      }
    }
  }
}
</style>
